import { getAuth } from "firebase/auth";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:4000/api";

/**
 * List properties for the authenticated owner (for Asset/Property Settings).
 * Backend may return stripeConnectIdOverride and/or stripeConnectIdDestination (which bank receives rent).
 * @returns {Promise<{ success: boolean, properties: Array<{ id, address, repairAutoApproveLimit?, emailMaintenanceTicketDetailsToVendors?, stripeConnectIdOverride?, stripeConnectIdDestination? }> }>}
 */
export async function getProperties() {
  const user = getAuth().currentUser;
  const token = user ? await user.getIdToken() : null;
  if (!token) throw new Error("Not authenticated");
  const res = await fetch(`${API_BASE_URL}/properties`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) throw new Error("Failed to fetch properties");
  return res.json();
}

/**
 * Update a property (e.g. repairAutoApproveLimit, emailMaintenanceTicketDetailsToVendors, stripeConnectIdOverride).
 * @param {string} propertyId
 * @param {Object} data - e.g. { repairAutoApproveLimit, emailMaintenanceTicketDetailsToVendors, stripeConnectIdOverride, showing_config, showing_link_slug }
 *   - stripeConnectIdOverride: null or '' = use landlord default; non-empty string = Stripe Connect account id (acct_xxx).
 *   - showing_config: { enabled, timezone, slotDurationMinutes, horizonDays, windows: [{ dayOfWeek, start, end }] }
 *   - showing_link_slug: optional custom public slug (lowercase); omitted to keep or auto-generate when enabling.
 */
export async function updateProperty(propertyId, data) {
  const user = getAuth().currentUser;
  const token = user ? await user.getIdToken() : null;
  if (!token) throw new Error("Not authenticated");
  const res = await fetch(`${API_BASE_URL}/properties/${propertyId}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    let body = null;
    try {
      body = await res.json();
    } catch {
      /* ignore */
    }
    const text =
      (body && (body.error || body.message)) ||
      `Failed to update property (${res.status})`;
    const err = new Error(text);
    err.status = res.status;
    err.code = body?.code;
    err.tier = body?.tier;
    err.cap = body?.cap;
    err.current = body?.current;
    err.body = body;
    throw err;
  }
  return res.json();
}
